import { Injectable } from '@angular/core';
import { getDocument, GlobalWorkerOptions, version as pdfjsVersion } from 'pdfjs-dist';
import { environment } from '../../../environments/environment';
import { UserService } from '../core/user.service';
import { FileTypeService } from './file-type.service';

/**
 * Single source of truth for "turn this file into a thumbnail image", used by
 * every upload widget (attachment panel, media-upload dialog, drag & drop
 * uploader) and every documents/gallery list that renders already-uploaded
 * files. Replaces three near-identical copies of this logic
 * (document-list-partial, drag-drop-upload's UploadPreviewService, the plain
 * FileReader preview in attachment/media-upload-dialog).
 *
 * - images  -> the file/URL itself is the thumbnail, nothing to generate
 * - videos  -> a frame grabbed from the video at the 10s mark
 * - PDFs    -> page 1 rendered to a canvas via pdf.js
 * - Word (.doc/.docx) of an uploaded document -> the same Syncfusion Document Editor the Word preview
 *             already uses, loaded on demand and run off-screen read-only: page 1 is exported as an image
 * - Spreadsheets (.xlsx/.xls/.xlsm/.ods of an uploaded document, .csv/.tsv) -> the first rows of the first
 *             sheet drawn as a small table on a canvas; the cells come from the same excel-to-json route the
 *             spreadsheet preview uses (csv/tsv are read straight from the file URL)
 * - HTML (.html/.htm) -> the page rendered off-screen in a locked-down iframe (no scripts, no network) and
 *             captured with html2canvas, which is loaded on demand
 * - anything else (ppt/cad/archive/…) -> no client-side thumbnail;
 *   caller falls back to the static per-extension icon (FileTypeService)
 *
 * Generation happens once per file (keyed by identity for a local `File`, by
 * URL for an already-uploaded document) and is cached for the life of the
 * service; concurrent requests for the same file share one in-flight promise.
 */
@Injectable({ providedIn: 'root' })
export class ThumbnailGeneratorService {
  private static readonly VIDEO_FRAME_SECONDS = 10;
  private static readonly JPEG_QUALITY = 0.75;
  private static readonly PDF_RENDER_SCALE = 0.5;
  private static workerConfigured = false;

  /** Width of a Word thumbnail; the page is scaled down from the editor's full-size render. */
  private static readonly WORD_THUMB_WIDTH = 240;
  /** Upper bound for the conversion request (the Import route calls an external service). */
  private static readonly WORD_IMPORT_TIMEOUT_MS = 60000;
  /** One off-screen editor at a time: each one builds a full layout engine. */
  private wordQueue: Promise<unknown> = Promise.resolve();

  /** Size of a spreadsheet thumbnail and how much of the sheet is drawn into it. */
  private static readonly SHEET_THUMB_WIDTH = 240;
  private static readonly SHEET_THUMB_HEIGHT = 340;
  private static readonly SHEET_MAX_ROWS = 14;
  private static readonly SHEET_MAX_COLS = 4;
  /** Upper bound for the workbook conversion request and for reading a csv. */
  private static readonly SHEET_FETCH_TIMEOUT_MS = 60000;
  /** A csv is only read up to this many characters: the thumbnail shows the first rows, not the file. */
  private static readonly CSV_MAX_CHARS = 65536;
  /** One spreadsheet at a time: the excel-to-json route converts the whole workbook for each call. */
  private sheetQueue: Promise<unknown> = Promise.resolve();

  /** An HTML page is laid out at this size (roughly A4) and scaled down to HTML_THUMB_WIDTH. */
  private static readonly HTML_PAGE_WIDTH = 800;
  private static readonly HTML_PAGE_HEIGHT = 1130;
  private static readonly HTML_THUMB_WIDTH = 240;
  /** Only the start of a page is read: the thumbnail shows the top of it. */
  private static readonly HTML_MAX_CHARS = 512 * 1024;
  private static readonly HTML_TIMEOUT_MS = 15000;
  /** One page at a time: each one gets its own iframe and canvas capture. */
  private htmlQueue: Promise<unknown> = Promise.resolve();

  private cache = new Map<string, string>();
  private inFlight = new Map<string, Promise<string | null>>();

  constructor(private fileTypeService: FileTypeService, private userService: UserService) {
    this.ensureWorkerConfigured();
  }

  /**
   * Generate (or return the cached) thumbnail for a file that's about to be
   * uploaded or sits in the upload queue.
   */
  generateForFile(file: File): Promise<string | null> {
    return this.withCache(this.fileCacheKey(file), () => this.buildForFile(file));
  }

  /**
   * Generate (or return the cached) thumbnail for a file that's already been
   * uploaded, identified by its download URL.
   */
  generateForUrl(
    url: string | null | undefined,
    fileName?: string | null,
    mimeType?: string | null,
    documentId?: string | number | null,
  ): Promise<string | null> {
    if (!url) return Promise.resolve(null);
    return this.withCache(url, () => this.buildForUrl(url, fileName, mimeType, documentId));
  }

  /**
   * True when this service can actually produce something for the file (vs. the caller falling back to a static icon).
   * Word files only qualify for an already-uploaded document (`documentId`), the same input the Word preview uses.
   */
  supportsClientThumbnail(fileName?: string | null, mimeType?: string | null, documentId?: string | number | null): boolean {
    const kind = this.fileTypeService.kindOf({ original_name: fileName, mime_type: mimeType });
    if (kind === 'word') return !!documentId;
    if (kind === 'excel') return this.isDelimitedText(fileName, mimeType) || !!documentId;
    if (kind === 'code') return this.isHtml(fileName, mimeType);
    return kind === 'image' || kind === 'video' || kind === 'pdf';
  }

  clearCache(): void {
    this.cache.clear();
  }

  private withCache(key: string, build: () => Promise<string | null>): Promise<string | null> {
    if (this.cache.has(key)) {
      return Promise.resolve(this.cache.get(key)!);
    }

    const existing = this.inFlight.get(key);
    if (existing) return existing;

    const promise = build()
      .then((result) => {
        if (result) this.cache.set(key, result);
        return result;
      })
      .catch((error) => {
        console.error('ThumbnailGeneratorService: generation failed', error);
        return null;
      })
      .finally(() => this.inFlight.delete(key));

    this.inFlight.set(key, promise);
    return promise;
  }

  private async buildForFile(file: File): Promise<string | null> {
    const kind = this.fileTypeService.kindOf({ original_name: file.name });

    if (kind === 'image') return this.readAsDataUrl(file);
    if (kind === 'video') return this.videoFrameFromObjectUrl(URL.createObjectURL(file), true);
    if (kind === 'pdf') return this.pdfFirstPageThumbnail(await file.arrayBuffer());
    if (kind === 'code' && this.isHtml(file.name, file.type)) {
      return this.htmlThumbnail((await file.text()).slice(0, ThumbnailGeneratorService.HTML_MAX_CHARS));
    }

    return null;
  }

  private async buildForUrl(
    url: string,
    fileName?: string | null,
    mimeType?: string | null,
    documentId?: string | number | null,
  ): Promise<string | null> {
    const kind = this.fileTypeService.kindOf({ original_name: fileName, mime_type: mimeType });

    // Images are already a thumbnail of themselves — nothing to generate.
    if (kind === 'image') return url;
    if (kind === 'video') return this.videoFrameFromObjectUrl(url, false);
    if (kind === 'pdf') return this.pdfFirstPageThumbnail(url);
    if (kind === 'word' && documentId) return this.wordFirstPageThumbnail(url, documentId);
    if (kind === 'excel') return this.spreadsheetThumbnail(url, fileName, mimeType, documentId);
    if (kind === 'code' && this.isHtml(fileName, mimeType)) return this.htmlThumbnailFromUrl(url);

    return null;
  }

  private isHtml(fileName?: string | null, mimeType?: string | null): boolean {
    const ext = this.fileTypeService.extensionOf({ original_name: fileName, mime_type: mimeType });
    return ext === 'html' || ext === 'htm';
  }

  private async htmlThumbnailFromUrl(url: string): Promise<string | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ThumbnailGeneratorService.HTML_TIMEOUT_MS);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return null;

      const html = (await response.text()).slice(0, ThumbnailGeneratorService.HTML_MAX_CHARS);
      return await this.htmlThumbnail(html);
    } catch (error) {
      console.warn('ThumbnailGeneratorService: HTML page could not be read', error);
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /** HTML thumbnails are built one at a time (see htmlQueue); a failure only ever resolves to null. */
  private htmlThumbnail(html: string): Promise<string | null> {
    const run = this.htmlQueue.then(() => this.renderHtmlThumbnail(html));
    this.htmlQueue = run.catch(() => null);
    return run;
  }

  /**
   * Renders an uploaded page off-screen and captures it. The page is untrusted, so it is locked down three ways:
   * the markup is re-serialised without scripts / frames / external stylesheets, the iframe is sandboxed without
   * `allow-scripts` (same-origin only, so html2canvas can read the laid-out DOM), and a CSP blocks every network
   * request (images only as data: URIs).
   */
  private async renderHtmlThumbnail(html: string): Promise<string | null> {
    let frame: HTMLIFrameElement | null = null;
    try {
      const page = new DOMParser().parseFromString(html, 'text/html');
      page.querySelectorAll('script, iframe, frame, object, embed, base, link[rel~="stylesheet"], meta[http-equiv]')
        .forEach((node) => node.remove());
      const policy = page.createElement('meta');
      policy.setAttribute('http-equiv', 'Content-Security-Policy');
      policy.setAttribute('content', "default-src 'none'; style-src 'unsafe-inline'; img-src data:");
      page.head.prepend(policy);

      // Loaded on demand: html2canvas is only needed once an HTML file shows up in a list.
      const { default: html2canvas } = await import('html2canvas');

      const width = ThumbnailGeneratorService.HTML_PAGE_WIDTH;
      const height = ThumbnailGeneratorService.HTML_PAGE_HEIGHT;
      frame = document.createElement('iframe');
      frame.setAttribute('sandbox', 'allow-same-origin');
      frame.style.cssText = `position:fixed;left:-10000px;top:0;width:${width}px;height:${height}px;border:0;pointer-events:none;`;

      const loaded = new Promise<void>((resolve, reject) => {
        frame!.onload = () => resolve();
        setTimeout(() => reject(new Error('HTML thumbnail frame did not load')), ThumbnailGeneratorService.HTML_TIMEOUT_MS);
      });
      frame.srcdoc = `<!doctype html>${page.documentElement.outerHTML}`;
      document.body.appendChild(frame);
      await loaded;

      const body = frame.contentDocument?.body;
      if (!body) return null;

      const canvas = await html2canvas(body, {
        width,
        height,
        windowWidth: width,
        windowHeight: height,
        scale: ThumbnailGeneratorService.HTML_THUMB_WIDTH / width,
        backgroundColor: '#ffffff', // JPEG has no transparency
        logging: false,
      });
      return canvas.toDataURL('image/jpeg', ThumbnailGeneratorService.JPEG_QUALITY);
    } catch (error) {
      console.warn('ThumbnailGeneratorService: HTML thumbnail unavailable', error);
      return null;
    } finally {
      frame?.remove();
    }
  }

  /** csv / tsv are plain text, so they don't need an uploaded document (no conversion route involved). */
  private isDelimitedText(fileName?: string | null, mimeType?: string | null): boolean {
    const ext = this.fileTypeService.extensionOf({ original_name: fileName, mime_type: mimeType });
    return ext === 'csv' || ext === 'tsv';
  }

  /** Spreadsheet thumbnails are built one at a time (see sheetQueue); a failure only ever resolves to null. */
  private spreadsheetThumbnail(
    url: string,
    fileName?: string | null,
    mimeType?: string | null,
    documentId?: string | number | null,
  ): Promise<string | null> {
    const run = this.sheetQueue.then(() => this.renderSpreadsheetThumbnail(url, fileName, mimeType, documentId));
    this.sheetQueue = run.catch(() => null);
    return run;
  }

  private async renderSpreadsheetThumbnail(
    url: string,
    fileName?: string | null,
    mimeType?: string | null,
    documentId?: string | number | null,
  ): Promise<string | null> {
    try {
      let grid: string[][] | null = null;
      if (this.isDelimitedText(fileName, mimeType)) {
        const ext = this.fileTypeService.extensionOf({ original_name: fileName, mime_type: mimeType });
        grid = await this.fetchDelimitedGrid(url, ext === 'tsv' ? '\t' : ',');
      } else if (documentId) {
        grid = await this.fetchWorkbookGrid(documentId);
      }
      if (!grid || !grid.length) return null;

      return this.drawSheetGrid(grid);
    } catch (error) {
      console.warn('ThumbnailGeneratorService: spreadsheet thumbnail unavailable', error);
      return null;
    }
  }

  /**
   * Same route the spreadsheet preview uses (POST spreadsheet/excel-to-json -> Syncfusion workbook JSON, for
   * xlsx / xls / xlsm / ods). Plain fetch with the app token, for the same reason as the Word import: a file
   * that can't be converted must stay silent and fall back to the icon, not raise the global error toast.
   */
  private async fetchWorkbookGrid(documentId: string | number): Promise<string[][] | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ThumbnailGeneratorService.SHEET_FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(`${environment.apiBaseUrl}/api/spreadsheet/excel-to-json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${this.userService.getToken()}`,
        },
        body: JSON.stringify({ documentId }),
        signal: controller.signal,
      });
      if (!response.ok) {
        console.warn(`ThumbnailGeneratorService: spreadsheet conversion returned HTTP ${response.status} for document ${documentId}`);
        return null;
      }

      const workbook = await response.json();
      const rows: any[] = workbook?.Workbook?.sheets?.[0]?.rows || [];
      const grid: string[][] = [];
      let rowIndex = 0;
      for (const row of rows) {
        // Syncfusion leaves empty rows / cells out and records the position in `index` instead.
        rowIndex = typeof row?.index === 'number' ? row.index : rowIndex;
        if (rowIndex >= ThumbnailGeneratorService.SHEET_MAX_ROWS) break;

        const cells: string[] = [];
        let cellIndex = 0;
        for (const cell of row?.cells || []) {
          cellIndex = typeof cell?.index === 'number' ? cell.index : cellIndex;
          if (cellIndex >= ThumbnailGeneratorService.SHEET_MAX_COLS) break;
          cells[cellIndex] = cell?.value === undefined || cell?.value === null ? '' : String(cell.value);
          cellIndex++;
        }
        grid[rowIndex] = Array.from(cells, (value) => value ?? '');
        rowIndex++;
      }
      return Array.from(grid, (row) => row ?? []);
    } finally {
      clearTimeout(timer);
    }
  }

  /** Reads only the start of a csv / tsv (the thumbnail needs the first rows, not the whole file). */
  private async fetchDelimitedGrid(url: string, delimiter: string): Promise<string[][] | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ThumbnailGeneratorService.SHEET_FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) return null;

      let text = '';
      const reader = response.body?.getReader();
      if (reader) {
        const decoder = new TextDecoder();
        while (text.length < ThumbnailGeneratorService.CSV_MAX_CHARS) {
          const { done, value } = await reader.read();
          if (done) break;
          text += decoder.decode(value, { stream: true });
        }
        reader.cancel().catch(() => undefined);
      } else {
        text = (await response.text()).slice(0, ThumbnailGeneratorService.CSV_MAX_CHARS);
      }

      return text
        .split(/\r?\n/)
        .filter((line) => line.length)
        .slice(0, ThumbnailGeneratorService.SHEET_MAX_ROWS)
        .map((line) => this.splitDelimitedLine(line, delimiter).slice(0, ThumbnailGeneratorService.SHEET_MAX_COLS));
    } finally {
      clearTimeout(timer);
    }
  }

  /** Splits one csv line, honouring "quoted, values" and "" escapes (a quoted line break is not supported). */
  private splitDelimitedLine(line: string, delimiter: string): string[] {
    const cells: string[] = [];
    let current = '';
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (quoted) {
        if (char === '"' && line[i + 1] === '"') { current += '"'; i++; }
        else if (char === '"') quoted = false;
        else current += char;
      } else if (char === '"') {
        quoted = true;
      } else if (char === delimiter) {
        cells.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    cells.push(current);
    return cells;
  }

  /** Draws the first rows of a sheet as a small table; the first row is styled as the header. */
  private drawSheetGrid(grid: string[][]): string | null {
    const width = ThumbnailGeneratorService.SHEET_THUMB_WIDTH;
    const height = ThumbnailGeneratorService.SHEET_THUMB_HEIGHT;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return null;

    const columns = Math.min(
      ThumbnailGeneratorService.SHEET_MAX_COLS,
      Math.max(1, ...grid.map((row) => row.length)),
    );
    const padding = 8;
    const rowHeight = 22;
    const columnWidth = (width - padding * 2) / columns;

    context.fillStyle = '#ffffff'; // JPEG has no transparency
    context.fillRect(0, 0, width, height);
    context.textBaseline = 'middle';

    grid.slice(0, ThumbnailGeneratorService.SHEET_MAX_ROWS).forEach((row, rowIndex) => {
      const top = padding + rowIndex * rowHeight;
      if (rowIndex === 0) {
        context.fillStyle = '#e8edf3';
        context.fillRect(padding, top, width - padding * 2, rowHeight);
      }
      context.font = `${rowIndex === 0 ? 'bold ' : ''}10px sans-serif`;

      for (let columnIndex = 0; columnIndex < columns; columnIndex++) {
        const left = padding + columnIndex * columnWidth;
        context.strokeStyle = '#d5dbe3';
        context.strokeRect(left, top, columnWidth, rowHeight);
        context.fillStyle = '#2b3440';
        context.fillText(this.fitText(context, row[columnIndex] || '', columnWidth - 8), left + 4, top + rowHeight / 2);
      }
    });

    return canvas.toDataURL('image/jpeg', ThumbnailGeneratorService.JPEG_QUALITY);
  }

  /** Shortens text with an ellipsis until it fits the cell. */
  private fitText(context: CanvasRenderingContext2D, text: string, maxWidth: number): string {
    if (context.measureText(text).width <= maxWidth) return text;
    let fitted = text;
    while (fitted.length > 1 && context.measureText(`${fitted}…`).width > maxWidth) {
      fitted = fitted.slice(0, -1);
    }
    return `${fitted}…`;
  }

  /** Word thumbnails are built one at a time (see wordQueue); a failure only ever resolves to null. */
  private wordFirstPageThumbnail(url: string, documentId: string | number): Promise<string | null> {
    const run = this.wordQueue.then(() => this.renderWordFirstPage(url, documentId));
    this.wordQueue = run.catch(() => null);
    return run;
  }

  /**
   * Same route the Word preview uses (POST syncfusion/documenteditor/Import -> SFDT, which returns a
   * document's saved SFDT when it has one), then an off-screen read-only Syncfusion editor exports page 1.
   * Plain fetch with the app token instead of HttpClient on purpose: a thumbnail that can't be built
   * (corrupt / password-protected / import service down) must stay silent and fall back to the icon,
   * not raise the global error toast.
   */
  private async renderWordFirstPage(url: string, documentId: string | number): Promise<string | null> {
    let host: HTMLDivElement | null = null;
    let editor: any = null;
    try {
      const sfdt = await this.fetchWordSfdt(url, documentId);
      if (!sfdt) return null;

      // Loaded on demand: the Document Editor is ~7.5 MB and must stay out of the eager bundle.
      const { DocumentEditor, Print } = await import('@syncfusion/ej2-angular-documenteditor');
      DocumentEditor.Inject(Print);

      host = document.createElement('div');
      host.style.cssText = 'position:fixed;left:-10000px;top:0;width:900px;height:1200px;pointer-events:none;';
      document.body.appendChild(host);

      editor = new DocumentEditor({ isReadOnly: true, enablePrint: true, enableSelection: false, width: '900px', height: '1200px' });
      editor.appendTo(host);
      editor.open(JSON.stringify(sfdt));

      // The layout is ready once the first page exists (open() lays out synchronously for SFDT; this is a safety net).
      for (let i = 0; i < 100 && !(editor.pageCount > 0); i++) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      if (!(editor.pageCount > 0)) return null;

      const page: HTMLImageElement | undefined = editor.exportAsImage(1, 'Jpeg');
      if (!page) return null;
      await page.decode();

      const canvas = document.createElement('canvas');
      const width = ThumbnailGeneratorService.WORD_THUMB_WIDTH;
      canvas.width = width;
      canvas.height = Math.round(width * (page.naturalHeight / page.naturalWidth));
      const context = canvas.getContext('2d');
      if (!context) return null;
      context.fillStyle = '#ffffff'; // JPEG has no transparency
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(page, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', ThumbnailGeneratorService.JPEG_QUALITY);
    } catch (error) {
      console.warn('ThumbnailGeneratorService: Word thumbnail unavailable', error);
      return null;
    } finally {
      try { editor?.destroy(); } catch { /* nothing left to clean up */ }
      host?.remove();
    }
  }

  private async fetchWordSfdt(url: string, documentId: string | number): Promise<unknown | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ThumbnailGeneratorService.WORD_IMPORT_TIMEOUT_MS);
    try {
      const response = await fetch(`${environment.apiBaseUrl}/api/syncfusion/documenteditor/Import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${this.userService.getToken()}`,
        },
        body: JSON.stringify({ fileUrl: url, documentId }),
        signal: controller.signal,
      });
      if (!response.ok) {
        console.warn(`ThumbnailGeneratorService: Word import returned HTTP ${response.status} for document ${documentId}`);
        return null;
      }
      return await response.json();
    } finally {
      clearTimeout(timer);
    }
  }

  private readAsDataUrl(file: File): Promise<string | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve((e.target?.result as string) ?? null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }

  private videoFrameFromObjectUrl(src: string, isObjectUrl: boolean): Promise<string | null> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve(null);
        return;
      }

      const cleanup = () => {
        clearTimeout(timeout);
        if (isObjectUrl) URL.revokeObjectURL(video.src);
        video.remove();
      };

      const timeout = setTimeout(() => {
        cleanup();
        resolve(null);
      }, 10000);

      video.preload = 'metadata';
      if (!isObjectUrl) video.crossOrigin = 'anonymous';
      video.src = src;

      video.addEventListener('loadedmetadata', () => {
        video.currentTime = Math.min(ThumbnailGeneratorService.VIDEO_FRAME_SECONDS, video.duration || 0);
      });

      video.addEventListener('seeked', () => {
        try {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', ThumbnailGeneratorService.JPEG_QUALITY);
          cleanup();
          resolve(dataUrl);
        } catch (error) {
          console.error('ThumbnailGeneratorService: video frame capture failed', error);
          cleanup();
          resolve(null);
        }
      });

      video.addEventListener('error', () => {
        cleanup();
        resolve(null);
      });
    });
  }

  private async pdfFirstPageThumbnail(source: string | ArrayBuffer): Promise<string | null> {
    try {
      const loadParams = typeof source === 'string' ? { url: source } : { data: source };
      const pdf = await this.withTimeout(getDocument(loadParams).promise, 10000);
      const page = await pdf.getPage(1);

      const canvas = document.createElement('canvas');
      const viewport = page.getViewport({ scale: ThumbnailGeneratorService.PDF_RENDER_SCALE });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const context = canvas.getContext('2d');
      if (!context) return null;

      await page.render({ canvasContext: context, viewport }).promise;
      return canvas.toDataURL('image/jpeg', ThumbnailGeneratorService.JPEG_QUALITY);
    } catch (error) {
      console.error('ThumbnailGeneratorService: PDF thumbnail failed', error);
      return null;
    }
  }

  /** Guards against a PDF render hanging forever if the pdf.js worker never loads (e.g. the CDN is unreachable). */
  private withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(`Timed out after ${ms}ms`)), ms);
      promise.then(
        (value) => { clearTimeout(timer); resolve(value); },
        (error) => { clearTimeout(timer); reject(error); },
      );
    });
  }

  private fileCacheKey(file: File): string {
    return `file:${file.name}_${file.size}_${file.lastModified}`;
  }

  private ensureWorkerConfigured(): void {
    if (ThumbnailGeneratorService.workerConfigured) return;
    // Must match the installed pdfjs-dist version exactly — pdf.js rejects a
    // main/worker version mismatch with a generic UnknownErrorException.
    GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.mjs`;
    ThumbnailGeneratorService.workerConfigured = true;
  }
}
